import { useState, useMemo, useCallback, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoxesStacked,
  faPenToSquare,
  faXmark,
  faSpinner,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import Title from "../shared/components/Title";
import FilterBar, {
  initialFilters,
} from "../features/difot-tools/components/FilterBar";
import ConsignmentRow from "../features/difot-tools/components/ConsignmentCard";
import BulkUpdatePanel from "../features/difot-tools/components/BulkUpdatePanel";
import Pagination from "../features/difot-tools/components/Pagination";
import { calculateKpiMeasure } from "../features/difot-tools/kpiMeasureUtils";
import { useAuth } from "../hooks/useAuth";
import apiClient from "../utils/apiClient";

const FILTER_OPTIONS_DEBOUNCE_MS = 400;

const TABLE_HEADERS = [
  { label: "MACHSHIP ID", cls: "w-[240px]" },
  { label: "DESPATCH DATE", cls: "w-[110px]" },
  { label: "CONNOTE NUMBER", cls: "w-[110px]" },
  { label: "PARENT COMPANY", cls: "w-[140px]" },
  { label: "CHILD COMPANY", cls: "w-[150px]" },
  { label: "SERVICE TYPE", cls: "w-[110px]" },
  { label: "CARRIER NAME", cls: "w-[130px]" },
  { label: "RECEIVER", cls: "w-[400px]" },
  { label: "COMPLETED DATE", cls: "w-[115px]" },
  { label: "LATEST SCANNED EVENT", cls: "w-[190px]" },
  { label: "ETA", cls: "w-[95px]" },
  { label: "SPECIAL INSTRUCTION", cls: "w-[150px]" },
  { label: "ATTACHMENT", cls: "w-[110px]" },
  { label: "KPI MEASURE", cls: "w-[95px] text-center" },
  { label: "SERVICE FLAGS", cls: "w-[150px]" },
  { label: "KPI HIT", cls: "w-[80px]" },
  { label: "PROCESSED?", cls: "w-[100px]" },
  { label: "UPDATE HISTORY", cls: "w-[110px]" },
  { label: "KPI EXPLANATION", cls: "w-[300px]" },
];

const initialFilterOptions = {
  parentCompanies: [],
  childCompanies: [],
  carriers: [],
  services: [],
  trackingStatuses: [],
  fromStates: [],
  toStates: [],
  receivers: [],
  portfolios: [],
  serviceFlags: [],
};

export default function DifotTools() {
  const { user } = useAuth();
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [filterOptionsData, setFilterOptionsData] = useState(initialFilterOptions);
  const [isFilterOptionsLoading, setIsFilterOptionsLoading] = useState(false);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [isReasonOptionsLoading, setIsReasonOptionsLoading] = useState(false);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [consignments, setConsignments] = useState([]);
  const [stats, setStats] = useState({ total: 0, hit: 0, miss: 0 });
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedConsignmentMap, setSelectedConsignmentMap] = useState(
    () => new Map()
  );
  const [isBulkDrawerOpen, setIsBulkDrawerOpen] = useState(false);
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // ── Selected consignments (for drawer) ──────────────────────────
  const selectedConsignments = useMemo(
    () =>
      Array.from(selectedIds)
        .map((id) => selectedConsignmentMap.get(id))
        .filter(Boolean),
    [selectedIds, selectedConsignmentMap]
  );
  const difotPercentage = useMemo(() => {
    const total = stats.total;
    if (total === 0) return "0.0%";
    return `${((stats.hit / total) * 100).toFixed(1)}%`;
  }, [stats]);

  // ── Selection ────────────────────────────────────────────────────
  const selectablePageIds = useMemo(
    () => consignments.map((c) => c.id),
    [consignments]
  );
  const allPageSelected =
    selectablePageIds.length > 0 &&
    selectablePageIds.every((id) => selectedIds.has(id));
  const hasCompleteDraftDateRange = Boolean(draftFilters.fromDate && draftFilters.toDate);

  useEffect(() => {
    let cancelled = false;

    const fetchReasonOptions = async () => {
      setIsReasonOptionsLoading(true);

      try {
        const response = await apiClient.get("/api/v1/difotTools/reasons");
        if (!response.ok) {
          throw new Error("Failed to load reason options");
        }

        const payload = await response.json();
        if (cancelled) return;
        setReasonOptions(Array.isArray(payload.reasons) ? payload.reasons : []);
      } catch {
        if (!cancelled) setReasonOptions([]);
      } finally {
        if (!cancelled) setIsReasonOptionsLoading(false);
      }
    };

    fetchReasonOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!hasCompleteDraftDateRange) {
      setFilterOptionsData(initialFilterOptions);
      setIsFilterOptionsLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const params = buildDifotQueryParams(draftFilters);

    const fetchFilterOptions = async () => {
      setIsFilterOptionsLoading(true);

      try {
        const response = await apiClient.get(
          `/api/v1/difotTools/filter-options?${params.toString()}`,
          { signal: controller.signal }
        );

        if (!response.ok) {
          throw new Error("Failed to load filter options");
        }

        const payload = await response.json();
        if (cancelled) return;

        setFilterOptionsData({
          parentCompanies: Array.isArray(payload.parentCompanies)
            ? payload.parentCompanies
            : [],
          childCompanies: Array.isArray(payload.childCompanies)
            ? payload.childCompanies
            : [],
          carriers: Array.isArray(payload.carriers) ? payload.carriers : [],
          services: Array.isArray(payload.services) ? payload.services : [],
          trackingStatuses: Array.isArray(payload.trackingStatuses)
            ? payload.trackingStatuses
            : [],
          fromStates: Array.isArray(payload.fromStates) ? payload.fromStates : [],
          toStates: Array.isArray(payload.toStates) ? payload.toStates : [],
          receivers: Array.isArray(payload.receivers) ? payload.receivers : [],
          portfolios: Array.isArray(payload.portfolios) ? payload.portfolios : [],
          serviceFlags: Array.isArray(payload.serviceFlags)
            ? payload.serviceFlags
            : [],
        });
      } catch (err) {
        if (err.name === "AbortError") return;
        if (cancelled) return;
        setFilterOptionsData(initialFilterOptions);
      } finally {
        if (!cancelled) setIsFilterOptionsLoading(false);
      }
    };

    const timeoutId = window.setTimeout(
      fetchFilterOptions,
      FILTER_OPTIONS_DEBOUNCE_MS
    );

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [hasCompleteDraftDateRange, draftFilters]);

  useEffect(() => {
    if (!hasAppliedFilters) {
      setConsignments([]);
      setStats({ total: 0, hit: 0, miss: 0 });
      setTotalItems(0);
      setTotalPages(0);
      setIsLoading(false);
      setErrorMessage("");
      return;
    }

    const controller = new AbortController();
    const params = buildDifotQueryParams(appliedFilters, {
      page: currentPage,
      pageSize,
    });

    let cancelled = false;

    const fetchConsignments = async () => {
      setIsLoading(true);
      setErrorMessage("");

      try {
        const response = await apiClient.get(
          `/api/v1/difotTools/consignments?${params.toString()}`,
          { signal: controller.signal }
        );

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.detail || err.error || "Failed to load consignments");
        }

        const payload = await response.json();
        if (cancelled) return;

        setConsignments(Array.isArray(payload.items) ? payload.items : []);
        setStats(
          payload.stats || { total: 0, hit: 0, miss: 0 }
        );
        setTotalItems(payload.pagination?.totalItems || 0);
        setTotalPages(payload.pagination?.totalPages || 0);
      } catch (err) {
        if (err.name === "AbortError") return;
        if (cancelled) return;
        setConsignments([]);
        setStats({ total: 0, hit: 0, miss: 0 });
        setTotalItems(0);
        setTotalPages(0);
        setErrorMessage(err.message || "Failed to load consignments");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchConsignments();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [appliedFilters, currentPage, pageSize, hasAppliedFilters, reloadKey]);

  useEffect(() => {
    if (consignments.length === 0) return;

    setSelectedConsignmentMap((prev) => {
      let next = prev;

      consignments.forEach((consignment) => {
        if (!selectedIds.has(consignment.id)) return;
        if (next === prev) next = new Map(prev);
        next.set(consignment.id, consignment);
      });

      return next;
    });
  }, [consignments, selectedIds]);

  const handleToggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

    setSelectedConsignmentMap((prev) => {
      const next = new Map(prev);

      if (next.has(id)) {
        next.delete(id);
        return next;
      }

      const consignment = consignments.find((item) => item.id === id);
      if (consignment) {
        next.set(id, consignment);
      }

      return next;
    });
  }, [consignments]);

  const handleSelectAllPage = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) selectablePageIds.forEach((id) => next.delete(id));
      else selectablePageIds.forEach((id) => next.add(id));
      return next;
    });

    setSelectedConsignmentMap((prev) => {
      const next = new Map(prev);

      if (allPageSelected) {
        selectablePageIds.forEach((id) => next.delete(id));
      } else {
        consignments.forEach((consignment) => next.set(consignment.id, consignment));
      }

      return next;
    });
  }, [allPageSelected, selectablePageIds, consignments]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setSelectedConsignmentMap(new Map());
    setIsBulkDrawerOpen(false);
  }, []);

  const handleBulkApply = useCallback(
    async (updates) => {
      const consignmentsToUpdate = selectedConsignments.filter((item) => item.msNumber);
      const consignmentNumbers = consignmentsToUpdate.map((item) => item.msNumber);

      if (consignmentNumbers.length === 0) {
        toast.error("Failed to save bulk update");
        return false;
      }

      try {
        setIsBulkSaving(true);
        const updatedBy = user?.name || user?.email || user?.username || undefined;
        const hasAdjustDays =
          updates.etaAdjustDays !== undefined || updates.completedAdjustDays !== undefined;
        const calculatedKpiMeasure =
          !hasAdjustDays && updates.etaDate && updates.completedDate
            ? calculateKpiMeasure({
                etaDate: updates.etaDate,
                completedDate: updates.completedDate,
              })
            : undefined;
        const finalKpiMeasure =
          updates.kpiMeasure !== undefined ? updates.kpiMeasure : calculatedKpiMeasure;

        const payload = {
          consignment_numbers: consignmentNumbers,
          result: {
            ...(updates.kpiHit !== undefined ? { kpi_hit: updates.kpiHit } : {}),
            ...(updates.reasonId !== undefined ? { reason_id: updates.reasonId } : {}),
            ...(updates.reason !== undefined ? { reason: updates.reason } : {}),
            ...(finalKpiMeasure !== undefined ? { kpi_measure: finalKpiMeasure } : {}),
            ...(updates.etaDate ? { eta_date: toIsoDate(updates.etaDate) } : {}),
            ...(updates.etaAdjustDays !== undefined
              ? { eta_adjust_days: updates.etaAdjustDays }
              : {}),
            ...(updates.etaDate ? { delivery_date: toIsoDate(updates.etaDate) } : {}),
            ...(updates.completedDate
              ? { completed_date: toIsoDate(updates.completedDate) }
              : {}),
            ...(updates.completedAdjustDays !== undefined
              ? { completed_adjust_days: updates.completedAdjustDays }
              : {}),
            updated_by: updatedBy,
          },
        };

        const response = await apiClient.post(
          "/api/v1/difotTools/consignments/bulk-update",
          payload
        );
        const result = await response.json().catch(() => ({}));

        if (!response.ok || result?.error || result?.success === false) {
          throw new Error(
            getDifotErrorMessage(
              result,
              `Failed to save bulk update${
                response.ok ? "" : ` (${response.status} ${response.statusText})`
              }`
            )
          );
        }

        const count = consignmentNumbers.length;
        setSelectedIds(new Set());
        setSelectedConsignmentMap(new Map());
        setReloadKey((prev) => prev + 1);
        setIsBulkDrawerOpen(false);
        toast.success(`Saved ${count} consignment${count !== 1 ? "s" : ""}`);
        return true;
      } catch (err) {
        toast.error(err.message || "Failed to save bulk update");
        return false;
      } finally {
        setIsBulkSaving(false);
      }
    },
    [selectedConsignments, user]
  );

  const handleFilterChange = useCallback(
    (newFilters) => setDraftFilters(newFilters),
    []
  );

  const handleApplyFilters = useCallback((overrideFilters) => {
    const toApply = overrideFilters || draftFilters;
    if (overrideFilters) setDraftFilters(overrideFilters);
    setAppliedFilters(toApply);
    setHasAppliedFilters(true);
    setCurrentPage(1);
    setSelectedIds(new Set());
    setSelectedConsignmentMap(new Map());
  }, [draftFilters]);

  const handleClearFilters = useCallback(() => {
    setDraftFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setHasAppliedFilters(false);
    setCurrentPage(1);
    setSelectedIds(new Set());
    setSelectedConsignmentMap(new Map());
  }, []);

  const handlePageChange = useCallback(
    (page) => setCurrentPage(Math.max(1, Math.min(page, totalPages))),
    [totalPages]
  );

  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  return (
    <div className="min-h-[calc(100vh-4rem)] px-4 sm:px-6 py-6">
      <Title title="DIFOT Tool" />

      {/* Filter bar (includes stats) */}
      <div className="mb-4">
        <FilterBar
          filters={draftFilters}
          appliedFilters={appliedFilters}
          filterOptionsData={filterOptionsData}
          isFilterOptionsLoading={isFilterOptionsLoading}
          areDependentFiltersDisabled={!hasCompleteDraftDateRange}
          onFilterChange={handleFilterChange}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
          stats={stats}
          difotPercentage={difotPercentage}
        />
      </div>

      {/* Bulk update drawer (fixed, slide-over) */}
      <BulkUpdatePanel
        isOpen={isBulkDrawerOpen}
        selectedCount={selectedIds.size}
        selectedConsignments={selectedConsignments}
        isApplying={isBulkSaving}
        reasonOptions={reasonOptions}
        isReasonOptionsLoading={isReasonOptionsLoading}
        onApply={handleBulkApply}
        onCancel={() => {
          if (!isBulkSaving) {
            setIsBulkDrawerOpen(false);
          }
        }}
      />

      {/* Selection bar */}
      {hasAppliedFilters && selectedIds.size > 0 && (
        <div className="mb-3 flex items-center gap-3 px-4 py-2.5 bg-[#144677] text-white rounded-xl shadow-sm">
          <FontAwesomeIcon icon={faPenToSquare} className="w-3.5 h-3.5 opacity-80" />
          <span className="text-sm font-semibold">
            {selectedIds.size} consignment{selectedIds.size !== 1 ? "s" : ""} selected
          </span>
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={() => setIsBulkDrawerOpen(true)}
              className="px-3 py-1.5 text-xs font-semibold bg-white text-[#144677] rounded-lg hover:bg-blue-50 transition-colors"
            >
              Edit
            </button>
            <button
              onClick={handleClearSelection}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white/80 hover:text-white border border-white/30 hover:border-white/60 rounded-lg transition-colors"
            >
              <FontAwesomeIcon icon={faXmark} className="w-3 h-3" />
              Clear
            </button>
          </div>
        </div>
      )}

      {hasAppliedFilters ? (
        <>
          {/* Table */}
          <div className="rounded-xl shadow-sm ring-1 ring-gray-200 overflow-hidden mb-4">
            {errorMessage ? (
              <div className="px-6 py-16 text-center">
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  className="w-10 h-10 text-amber-300 mb-3"
                />
                <p className="text-sm font-semibold text-gray-600">
                  Failed to load DIFOT data.
                </p>
                <p className="mt-1 text-xs text-gray-400">{errorMessage}</p>
              </div>
            ) : isLoading ? (
              <div className="px-6 py-16 text-center">
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="mb-3 h-8 w-8 animate-spin text-[#144677]"
                />
                <p className="text-sm font-semibold text-gray-600">
                  Loading consignments...
                </p>
              </div>
            ) : (
            <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-220px)]">
              <table className="min-w-full border-collapse table-fixed">
                {/* ── Header ─────────────────────────────────────────── */}
                <thead className="sticky top-0 z-20">
                  <tr className="bg-[#144677] text-white text-[10px] font-bold uppercase tracking-wider">
                    {/* Checkbox */}
                    <th className="sticky left-0 bg-[#144677] w-10 px-3 py-3 z-10">
                      <input
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={handleSelectAllPage}
                        disabled={selectablePageIds.length === 0}
                        className="difot-checkbox"
                      />
                    </th>
                    {/* Data columns */}
                    {TABLE_HEADERS.map((h) => (
                      <th
                        key={h.label}
                        className={`px-3 py-3 text-left ${h.cls}`}
                      >
                        {h.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                {/* ── Body ───────────────────────────────────────────── */}
                <tbody className="bg-white divide-y divide-gray-50">
                  {consignments.length === 0 ? (
                    <tr>
                      <td
                        colSpan={TABLE_HEADERS.length + 1}
                        className="px-6 py-16 text-center"
                      >
                        <FontAwesomeIcon
                          icon={faBoxesStacked}
                          className="w-10 h-10 text-gray-200 mb-3"
                        />
                        <p className="text-gray-400 text-sm">
                          No consignments match your filters.
                        </p>
                        <button
                          onClick={handleClearFilters}
                          className="mt-3 text-xs font-semibold text-[#144677] hover:underline"
                        >
                          Clear all filters
                        </button>
                      </td>
                    </tr>
                  ) : (
                    consignments.map((consignment) => (
                      <ConsignmentRow
                        key={consignment.id}
                        consignment={consignment}
                        isSelected={selectedIds.has(consignment.id)}
                        reasonOptions={reasonOptions}
                        onToggleSelect={handleToggleSelect}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
            )}
          </div>

          {/* Pagination */}
          {!isLoading && !errorMessage && totalItems > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalRecords={totalItems}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </>
      ) : (
        <div className="mb-4 rounded-xl border border-dashed border-gray-300 bg-white px-6 py-14 text-center shadow-sm">
          <FontAwesomeIcon
            icon={faBoxesStacked}
            className="mb-3 h-10 w-10 text-gray-200"
          />
          <p className="text-sm font-semibold text-gray-600">
            Apply filters to load the DIFOT table.
          </p>
          <p className="mt-1 text-xs text-gray-400">
            Table results will stay hidden until you click Apply.
          </p>
        </div>
      )}
    </div>
  );
}

function toIsoDate(value) {
  if (!value) return "";
  const [day = "", month = "", year = ""] = value.split("/");
  if (!day || !month || !year) return value;
  return `${year}-${month}-${day}`;
}

function getDifotErrorMessage(payload, fallback) {
  if (!payload || typeof payload !== "object") return fallback;

  if (typeof payload.detail === "string") return payload.detail;
  if (Array.isArray(payload.detail) && payload.detail.length > 0) {
    return payload.detail
      .map((item) => item?.msg || item?.message || JSON.stringify(item))
      .filter(Boolean)
      .join("; ");
  }
  if (typeof payload.message === "string") return payload.message;
  if (typeof payload.error === "string") return payload.error;

  const failedResult = Array.isArray(payload.result)
    ? payload.result.find((item) => item && item.success === false)
    : null;
  const nestedResult = failedResult?.result;
  if (typeof nestedResult?.message === "string") return nestedResult.message;
  if (typeof nestedResult?.error === "string") return nestedResult.error;

  return fallback;
}

function buildDifotQueryParams(filters, pagination = {}) {
  const params = new URLSearchParams();

  if (pagination.page) params.set("page", String(pagination.page));
  if (pagination.pageSize) params.set("page_size", String(pagination.pageSize));

  if (filters.fromDate) params.set("from_date", toIsoDate(filters.fromDate));
  if (filters.toDate) params.set("to_date", toIsoDate(filters.toDate));
  if (filters.kpiHit !== null) params.set("kpi_hit", String(filters.kpiHit));
  if (filters.isProcessed !== null) {
    params.set("is_processed", String(filters.isProcessed));
  }
  if (filters.completedOnly) params.set("completed_only", "true");
  if (filters.trackingSearch?.length > 0) {
    params.set("tracking", filters.trackingSearch.map((o) => o.value).join(","));
  }
  if (filters.reasonSearch) params.set("reason", filters.reasonSearch);
  if (filters.parentCompanySearch?.length > 0) {
    params.set("parent_company", filters.parentCompanySearch.map((o) => o.value).join(","));
  }
  if (filters.companySearch?.length > 0) {
    params.set("company", filters.companySearch.map((o) => o.value).join(","));
  }
  if (filters.carrierSearch?.length > 0) {
    params.set("carrier", filters.carrierSearch.map((o) => o.value).join(","));
  }
  if (filters.serviceSearch?.length > 0) {
    params.set("service", filters.serviceSearch.map((o) => o.value).join(","));
  }
  if (filters.fromStateSearch?.length > 0) {
    params.set("from_state", filters.fromStateSearch.map((o) => o.value).join(","));
  }
  if (filters.toStateSearch?.length > 0) {
    params.set("to_state", filters.toStateSearch.map((o) => o.value).join(","));
  }
  if (filters.receiverSearch?.length > 0) {
    params.set("receiver", filters.receiverSearch.map((o) => o.value).join(","));
  }
  if (filters.serviceFlag) params.set("service_flag", filters.serviceFlag);
  if (filters.msNumber) params.set("ms_number", filters.msNumber);
  if (filters.connote) params.set("connote", filters.connote);
  if (filters.portfolio?.length > 0) {
    params.set(
      "portfolio",
      filters.portfolio.map((item) => item.value).join(",")
    );
  }
  if (filters.kpiMeasureMin !== "" && filters.kpiMeasureMin !== null) {
    params.set("kpi_measure_min", String(filters.kpiMeasureMin));
  }
  if (filters.kpiMeasureMax !== "" && filters.kpiMeasureMax !== null) {
    params.set("kpi_measure_max", String(filters.kpiMeasureMax));
  }

  return params;
}
